import { ComponentProps, useEffect, useRef } from 'react';
import { Interpolation, Theme, css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { PopoverMenu, XIcon, usePopoverMenu } from '@frontend/design-system';
import { backgroundColor } from '../utils/constants';
import { formatPhoneNumber } from '../utils/phone-utils';
import { ReturnUseDialerProps } from './use-dialer-types';

export type DialerProps = {
  className?: string;
  customStyles?: Interpolation<Theme>;
  focusOnMount?: boolean;
  placeholderLabel?: string;
  currentDial: string | undefined;
  setCurrentDial: (value: string | undefined) => void;
  render?: (props: { getItemProps: (index: number, action: () => void) => ComponentProps<'li'> }) => JSX.Element;
} & ReturnUseDialerProps;

export const DialerInput = ({
  className,
  customStyles,
  inputState,
  inputRef,
  dispatch,
  handleKeyDown,
  focusOnMount = true,
  placeholderLabel,
  currentDial,
  setCurrentDial,
  render,
}: DialerProps) => {
  const container = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('generic-dialpad-accessories');

  const maskedDial = formatPhoneNumber(currentDial ?? '');

  useEffect(() => {
    if (focusOnMount) {
      inputRef.current?.focus();
    }
  }, []);

  const listActionsRef = useRef<(() => void)[]>([]);

  const { activeIndex, isOpen, open, getTriggerProps, getMenuProps, getItemProps } = usePopoverMenu<HTMLInputElement>({
    placement: 'bottom',
    middlewareOptions: {
      offset: 0,
    },
    interactionOptions: {
      listNavigation: {
        virtual: true,
        scrollItemIntoView: {
          block: 'center',
        },
      },
      typeahead: { enabled: false },
    },
  });

  const triggerProps = getTriggerProps({
    onKeyDown: (e) => {
      handleKeyDown(e, activeIndex === null);

      // Since we're navigating virtually, we need to trigger the selected item's action on the input itself
      if (e.key === 'Enter' && activeIndex !== null) {
        listActionsRef.current[activeIndex]?.();
      } else if (!isOpen) {
        open();
      }
    },
    onFocus: () => dispatch({ type: 'focus' }),
    onPaste: (e: ClipboardEvent) => {
      const pastedData = e.clipboardData?.getData('text') || '';
      dispatch({ type: 'set', payload: pastedData });
      e.preventDefault();
    },
    // @ts-expect-error - the types are typed to the design system, we don't care about that here
    onChange: (e: ClipboardEvent) => setCurrentDial(e.target.value),
  });

  return (
    <div ref={container} className={className} css={!!customStyles ? customStyles : style}>
      <label htmlFor='transfer-address'></label>
      <div className='input-wrap'>
        <input
          {...triggerProps}
          ref={(node) => {
            if (node) {
              inputRef.current = node;
              triggerProps.ref?.(node);
            }
          }}
          autoComplete='off'
          autoFocus
          type='text'
          name='dialer-input'
          placeholder={placeholderLabel ? placeholderLabel : t('Name or Number')}
          aria-invalid={!!inputState.error && inputState.blurred}
          value={maskedDial ?? ''}
          data-trackingid='generic-dialer-input'
        />
        {!!currentDial && <XIcon className='x-icon' onMouseDown={() => dispatch({ type: 'reset' })} size={16} />}
      </div>
      <PopoverMenu
        css={{ padding: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
        {...getMenuProps()}
        initialFocus={-1}
      >
        {render?.({
          getItemProps: (index, action) => {
            listActionsRef.current[index] = action;
            return {
              ...getItemProps({
                index,
                onClick: () => {
                  // We don't actually need this function, because the items will already have their own click handlers.
                  // This noop is just to satisfy the type system.
                },
              }),
            };
          },
        })}
      </PopoverMenu>
      {inputState.blurred && inputState.error && <p className='error-message'>{inputState.error}</p>}
    </div>
  );
};

const style = css`
  margin: auto;
  display: block;
  text-align: left;
  width: 90%;
  input {
    text-align: center;
    width: 100%;
    height: ${theme.spacing(6)};
    padding: ${theme.spacing(1.5)} ${theme.spacing(3)};
    box-sizing: border-box;
    border-radius: ${theme.borderRadius.small};
    background: ${backgroundColor};
    outline: none;
    font-size: ${theme.fontSize(20)};
    font-weight: ${theme.font.weight.bold};
    color: ${theme.colors.text.white};
    margin-top: 2px;
    border: 1px solid ${theme.colors.neutral30};
    &:focus {
      border: 1px solid ${theme.colors.primary50};
    }
    &[aria-invalid='true'] {
      border: 1px solid ${theme.colors.critical30};
    }
    &.large {
      font-size: 1.8em;
    }
    &.medium {
      font-size: 1.5em;
    }
    &.small {
      font-size: 1.2em;
    }
    &::placeholder {
      color: ${theme.colors.neutral30};
      font-weight: bold;
    }
  }
  .input-wrap {
    position: relative;
    .x-icon {
      position: absolute;
      right: ${theme.spacing(1.25)};
      top: ${theme.spacing(2.25)};
      padding: 1px;
      cursor: pointer;
      color: ${theme.colors.neutral20};
      &:hover {
        color: ${theme.colors.white};
      }
    }
  }
  .error-message {
    color: ${theme.colors.critical30};
    margin-top: ${theme.spacing(0.5)};
    font-size: ${theme.fontSize(12)};
  }
`;
